import { Card } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';

import * as ROUTES from '@constants/routes';

import type { RelatedPersonRecord } from '@models/related-person';

import { formatPhone } from '@utils/format';

import DirectivesEntry from '../Directives/DirectivesEntry';
import { SectionH2 } from '../HealthSummary.style';

const messages = defineMessages({
  powerOfAttorney: {
    id: 'contacts.powerOfAttorney',
    defaultMessage: 'Power of Attorney',
  },
  emergencyContact: {
    id: 'contacts.emergencyContact',
    defaultMessage: 'Emergency Contact',
  },
  primaryEmergencyContact: {
    id: 'contacts.primaryEmergencyContact',
    defaultMessage: 'Primary Emergency Contact',
  },
  secondaryEmergencyContact: {
    id: 'contacts.secondaryEmergencyContact',
    defaultMessage: 'Secondary Emergency Contact',
  },
  title: {
    id: 'contacts.title',
    defaultMessage: 'Emergency Contacts',
  },
});

const EMERGENCY_CONTACT_COUNT = 5;

interface ContactsProps {
  related_persons?: RelatedPersonRecord[];
  clickable?: boolean;
  loading?: boolean;
}

function Contacts({
  // clickable = true,
  loading = false,
  related_persons = [],
}: ContactsProps) {
  const { formatMessage } = useIntl();

  const { emergencyContacts, powerOfAttorney } = related_persons.reduce<{
    emergencyContacts: RelatedPersonRecord[];
    powerOfAttorney?: RelatedPersonRecord | null;
  }>(
    (acc, curr) => {
      if (curr.isEmergencyContact()) {
        acc['emergencyContacts'].push(curr);
      } else if (curr.isPowerOfAttorney() && !acc['powerOfAttorney']) {
        acc['powerOfAttorney'] = curr;
      }
      return acc;
    },
    {
      emergencyContacts: [],
      powerOfAttorney: null,
    }
  );

  const clickable = false;
  const limitedEmergContacts = emergencyContacts.slice(
    0,
    EMERGENCY_CONTACT_COUNT
  );

  return (
    <Card sx={{ p: 2.5 }}>
      <ContentLoader loading={loading}>
        <>
          <SectionH2>{formatMessage(messages.title).toUpperCase()}</SectionH2>

          {limitedEmergContacts.map(({ name, phone }, index) => {
            const title =
              index === 0
                ? messages.primaryEmergencyContact
                : index === 1
                  ? messages.secondaryEmergencyContact
                  : messages.emergencyContact;
            return (
              <DirectivesEntry
                clickable={clickable}
                data={
                  name ? [`${name}`, `${phone ? formatPhone(phone) : ''}`] : []
                }
                key={`emergency-contact-${index}`}
                route={ROUTES.CONTACTS}
                title={formatMessage(title)}
              />
            );
          })}

          <DirectivesEntry
            clickable={clickable}
            data={
              powerOfAttorney?.name
                ? [
                    `${powerOfAttorney.name}`,
                    `${
                      powerOfAttorney.phone
                        ? formatPhone(powerOfAttorney.phone)
                        : ''
                    }`,
                  ]
                : []
            }
            route={ROUTES.LIVING_WILL}
            title={formatMessage(messages.powerOfAttorney)}
          />
        </>
      </ContentLoader>
    </Card>
  );
}

export default Contacts;
